import React, { FC, useState, useEffect, useMemo } from 'react';

import { Alert, Box, Button, Typography } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import { BackflowJobGroup } from '../../model';
import { Customer } from '../../../salesEstimateModules/estimateDetails/model';

import RequiredFieldRow from '../../components/RequiredFieldRow';

import axios from '../../../sharedModules/utils/axios.utils';

const AUTHORITY_PROVIDED_KEYS: (keyof Customer)[] = ['u_wtractno', 'u_wtrsrvco'];

interface FieldRequirements {
  requiresAccountNumber: boolean;
  requiresServiceNumber: boolean;
}

interface AuthorityProvidedFieldsFormProps {
  fieldRequirements: FieldRequirements;
  formValues: Customer;
  setFormValues: React.Dispatch<React.SetStateAction<Customer>>;
  submitting: boolean;
  hasChanges: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

const AuthorityProvidedFieldsForm: FC<AuthorityProvidedFieldsFormProps> = ({
  fieldRequirements,
  formValues,
  setFormValues,
  submitting,
  hasChanges,
  onSubmit,
}) => (
  <form onSubmit={onSubmit}>
    <RequiredFieldRow
      label="Authority Account Number"
      value={formValues.u_wtractno || ''}
      onChange={(value) => setFormValues({ ...formValues, u_wtractno: value })}
      required={fieldRequirements.requiresAccountNumber}
      disabled={submitting}
      maxLength={20}
    />
    <RequiredFieldRow
      label="Authority Service Number"
      value={formValues.u_wtrsrvco || ''}
      onChange={(value) => setFormValues({ ...formValues, u_wtrsrvco: value })}
      required={fieldRequirements.requiresServiceNumber}
      disabled={submitting}
      maxLength={20}
    />
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        startIcon={<SaveIcon />}
        disabled={submitting || !hasChanges}
      >
        {submitting ? 'Saving...' : 'Save'}
      </Button>
    </Box>
  </form>
);

interface AuthorityProvidedFieldsProps {
  backflowJobGroup: BackflowJobGroup;
  fetchBackflowJobGroup: () => void;
}

const AuthorityProvidedFields: FC<AuthorityProvidedFieldsProps> = (props) => {
  const { backflowJobGroup, fetchBackflowJobGroup } = props;

  const [customer, setCustomer] = useState<Customer>({});
  const [formValues, setFormValues] = useState<Customer>({});
  const [fieldRequirements, setFieldRequirements] = useState<FieldRequirements>({
    requiresAccountNumber: false,
    requiresServiceNumber: false,
  });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const [submitting, setSubmitting] = useState<boolean>(false);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/sales_estimates/customers/${backflowJobGroup?.customer_id}`);
      const result = response.data.result;

      setFormValues({ u_wtractno: result?.u_wtractno, u_wtrsrvco: result?.u_wtrsrvco });
      setCustomer(result);
      setFieldRequirements({
        requiresAccountNumber: Boolean(result?.requires_account_number),
        requiresServiceNumber: Boolean(result?.requires_service_number),
      });

      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong fetching Customer Info');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await axios.put(`/sales_estimates/customers/${backflowJobGroup?.customer_id}`, {
        customer: formValues,
      });

      setSuccessMessage('Authority-Provided Fields updated successfully');
      fetchBackflowJobGroup();
    } catch (error) {
      setErrorMessage('Something went wrong updating the Authority-Provided Fields');
    } finally {
      setSubmitting(false);
    }
  };

  const hasChanges = useMemo(
    () => AUTHORITY_PROVIDED_KEYS.some((key) => formValues[key] !== customer[key]),
    [formValues, customer],
  );

  const fieldsRequired = fieldRequirements.requiresAccountNumber || fieldRequirements.requiresServiceNumber;

  useEffect(() => {
    if (backflowJobGroup?.customer_id) fetchCustomer();
  }, [backflowJobGroup?.customer_id]);

  return (
    <LoadingWrapper isLoading={loading} message="Loading">
      <ErrorMessage message={errorMessage} />
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="600">
          Authority-Provided Fields
        </Typography>
      </Box>

      <AuthorityProvidedFieldsForm
        fieldRequirements={fieldRequirements}
        formValues={formValues}
        setFormValues={setFormValues}
        submitting={submitting}
        hasChanges={hasChanges}
        onSubmit={handleFormSubmit}
      />
    </LoadingWrapper>
  );
};

export default AuthorityProvidedFields;
